import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { connections } from '../../../api/integrations'

export const IntegrationAuthContext = createContext({
  isAuthenticated: false,
  isLoading: false,
  tokenData: null,
  refetchToken: () => {},
  integration: null
})

export const useIntegrationAuth = () => {
  const context = useContext(IntegrationAuthContext)
  if (!context) {
    throw new Error('useIntegrationAuth must be used within IntegrationAuthProvider')
  }
  return context
}

export const useIntegrationAuthState = (integration, options = {}) => {
  const { enabled = !!integration } = options

  const [authDialogOpen, setAuthDialogOpen] = useState(false)

  // `useListUserConnectionTokens` performs a silent server-side OAuth refresh
  // inline when it encounters a row flagged `requiresReauth: true` (see
  // `listUserConnectionTokensWithSilentRefresh` in `api/integrations.js`).
  // That means by the time `userTokensData` resolves here, the row is either:
  //   - cleanly authenticated (refresh succeeded, flag cleared, new expiration)
  //   - still flagged (refresh token itself is dead → user really must reconnect)
  // React-query's queryKey dedup ensures N IntegrationAuthProviders for the
  // same domain on one page coalesce into a single refresh attempt.
  const {
    data: userTokensData,
    isFetching: isFetchingUserTokens,
    error: userTokensError,
    refetch: refetchUserTokens
  } = connections.useListUserConnectionTokens(integration, { enabled })

  const {
    data: exposedData,
    isFetching: isFetchingExposed,
    error: exposedError,
    refetch: refetchExposed
  } = connections.useListExposedConnections(integration, { enabled })

  const tokenData = useMemo(() => {
    const tokens = userTokensData?.tokens || []
    return tokens.find(t => t.domain === integration) || null
  }, [userTokensData, integration])

  const exposedConnection = useMemo(() => {
    const exposed = exposedData?.connections || []
    return exposed.find(c => c.domain === integration) || null
  }, [exposedData, integration])

  const authInputs = useMemo(() => exposedConnection?.authInputs || null, [exposedConnection])
  const domainName = useMemo(() => exposedConnection?.domainName || null, [exposedConnection])

  const isLoading = isFetchingUserTokens || isFetchingExposed
  const error = userTokensError || exposedError

  // Treat a `requiresReauth: true` row as NOT authenticated. The silent
  // refresh already had its shot inside the list queryFn; a row that still
  // arrives here flagged means the refresh failed (or was already exhausted
  // this session) and the user genuinely needs to reconnect. Pre-fix, the
  // gate only checked `!!tokenData`, so flagged rows would let smart
  // components render against a known-dead token and only fail at the
  // downstream API call.
  const tokenIsUsable = !!tokenData && !tokenData.requiresReauth
  const isAuthenticated = useMemo(() => tokenIsUsable && !error, [tokenIsUsable, error])
  const requiresAuth = useMemo(() => !isLoading && !isAuthenticated && !!authInputs, [isLoading, isAuthenticated, authInputs])

  const refetch = useCallback(() => {
    refetchUserTokens()
    refetchExposed()
  }, [refetchUserTokens, refetchExposed])

  const handleConnectClick = useCallback(() => {
    setAuthDialogOpen(true)
  }, [])

  const handleAuthSuccess = useCallback(() => {
    refetch()
  }, [refetch])

  const contextValue = useMemo(() => ({
    isAuthenticated,
    isLoading,
    tokenData,
    refetchToken: refetch,
    integration
  }), [isAuthenticated, isLoading, tokenData, refetch, integration])

  return {
    isLoading,
    isAuthenticated,
    requiresAuth,
    error,
    tokenData,
    authInputs,
    domainName,
    refetch,
    contextValue,
    authDialogOpen,
    setAuthDialogOpen,
    handleConnectClick,
    handleAuthSuccess,
    integration
  }
}
